
import React from 'react';
import { AlertTriangle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface MapErrorHandlerProps {
  error: string | null;
  domainError: boolean;
}

const MapErrorHandler: React.FC<MapErrorHandlerProps> = ({ error, domainError }) => {
  const [showDebugInfo, setShowDebugInfo] = React.useState(false);
  const currentDomain = window.location.hostname;

  if (!error) return null;

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{domainError ? "Google Maps Domain Restriction Error" : "Google Maps Error"}</AlertTitle>
      <AlertDescription>
        {error}
        <div className="mt-2">
          <button 
            onClick={() => setShowDebugInfo(!showDebugInfo)} 
            className="text-xs underline mr-2"
          >
            {showDebugInfo ? "Hide Debug Info" : "Show Debug Info"}
          </button>
          
          {showDebugInfo && domainError && (
            <div className="mt-4 text-xs p-3 bg-gray-100 rounded">
              <p className="font-medium mb-2">Domain Restriction Error Details:</p>
              <p><strong>Current domain:</strong> {currentDomain}</p>
              
              <div className="mt-3 bg-white p-3 rounded shadow-sm">
                <p className="font-medium mb-1 text-red-600">How to fix this error:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Go to the <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                  <li>Select your project and find your API key</li>
                  <li>Click on the API key to edit its settings</li>
                  <li>Under "Application restrictions", select "HTTP referrers"</li>
                  <li>Add the following domains to the allowed list:
                    <div className="bg-gray-100 p-2 mt-1 rounded font-mono text-[10px] break-all">
                      <div>{currentDomain}/*</div>
                      <div>*.lovable.app/*</div>
                      <div>*.lovable.dev/*</div>
                    </div>
                  </li>
                  <li>Click Save, then refresh this page</li>
                </ol>
              </div>
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.location.reload()}
            className="text-xs mt-2"
          >
            Refresh Page
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default MapErrorHandler;
